/**
 * Physically-motivated bloom.
 *
 *  - soft-knee threshold applied *after* exposure, so the bloom threshold is an
 *    EV, not an arbitrary linear number that changes meaning as the scene grades
 *  - Karis average on the first downsample (kills single-texel fireflies)
 *  - 6-7 mip progressive downsample (COD 13-tap) / upsample (9-tap tent)
 *  - optional anamorphic streak built from mip 2
 *
 * owner: render
 */
import * as THREE from 'three';
import { Blitter, makeMaterial, makeRT, GLSL_COMMON } from './Common.js';

const PREFILTER_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tColor;
uniform sampler2D tExposure;
uniform float uPreExposure;  // lighting's exposure anchor (PostFX._baseExposure)
uniform vec2 uTexel;         // source texel
uniform float uThreshold;
uniform float uKnee;
uniform float uClamp;
uniform float uFirefly;      // how far above the local median a texel may sit
varying vec2 vUv;

vec3 fetch(vec2 uv, float ev) {
  return min(max(texture2D(tColor, uv).rgb, vec3(0.0)) * ev, vec3(uClamp));
}
float karis(vec3 c) { return 1.0 / (1.0 + pfLuma(c)); }

/* median of three — the cheap building block of a 13-tap soft median */
float med3(float a, float b, float c) { return max(min(a, b), min(max(a, b), c)); }

void main() {
  // Total exposure: the anchor the frame was metered against times the
  // histogram's residual. The threshold below is an EV, so it must see both.
  float ev = texture2D(tExposure, vec2(0.5)).r * uPreExposure;
  vec2 t = uTexel;

  vec3 A = fetch(vUv + t * vec2(-2.0, -2.0), ev);
  vec3 B = fetch(vUv + t * vec2( 0.0, -2.0), ev);
  vec3 C = fetch(vUv + t * vec2( 2.0, -2.0), ev);
  vec3 D = fetch(vUv + t * vec2(-1.0, -1.0), ev);
  vec3 E = fetch(vUv + t * vec2( 1.0, -1.0), ev);
  vec3 F = fetch(vUv + t * vec2(-2.0,  0.0), ev);
  vec3 G = fetch(vUv, ev);
  vec3 H = fetch(vUv + t * vec2( 2.0,  0.0), ev);
  vec3 I = fetch(vUv + t * vec2(-1.0,  1.0), ev);
  vec3 J = fetch(vUv + t * vec2( 1.0,  1.0), ev);
  vec3 K = fetch(vUv + t * vec2(-2.0,  2.0), ev);
  vec3 L = fetch(vUv + t * vec2( 0.0,  2.0), ev);
  vec3 M = fetch(vUv + t * vec2( 2.0,  2.0), ev);

  vec3 g0 = (D + E + I + J) * 0.25;
  vec3 g1 = (A + B + F + G) * 0.25;
  vec3 g2 = (B + C + G + H) * 0.25;
  vec3 g3 = (F + G + K + L) * 0.25;
  vec3 g4 = (G + H + L + M) * 0.25;

  float w0 = karis(g0) * 0.5;
  float w1 = karis(g1) * 0.125;
  float w2 = karis(g2) * 0.125;
  float w3 = karis(g3) * 0.125;
  float w4 = karis(g4) * 0.125;
  vec3 c = (g0 * w0 + g1 * w1 + g2 * w2 + g3 * w3 + g4 * w4) / max(w0 + w1 + w2 + w3 + w4, 1e-5);

  /* Firefly clamp.
     The Karis average only defeats a *single* hot texel. An 8-15 px specular
     spark survives it intact, gets smeared across seven mips and lands as an
     isolated white dot with a halo — the classic firefly. Compare this tap's
     brightness against the median of the surrounding 3x3-ish ring and refuse to
     let it exceed that median by more than uFirefly. Isolated sparks collapse;
     a genuinely bright *area* (sun, sky through a doorway, muzzle flash) has a
     bright median too and passes through untouched. */
  float nb = med3(med3(pfLuma(g1), pfLuma(g2), pfLuma(g3)),
                  pfLuma(g4), pfLuma(g0));
  float lum = pfLuma(c);
  float cap = nb * uFirefly + uThreshold * 0.5;
  if (lum > cap) c *= cap / max(lum, 1e-5);

  float br = maxc(c);
  float rq = clamp(br - uThreshold + uKnee, 0.0, 2.0 * uKnee);
  rq = rq * rq / (4.0 * uKnee + 1e-4);
  float contrib = max(rq, br - uThreshold) / max(br, 1e-4);
  gl_FragColor = vec4(c * contrib, 1.0);
}
`;

const DOWN_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tColor;
uniform vec2 uTexel;
varying vec2 vUv;
void main() {
  vec2 t = uTexel;
  vec3 A = texture2D(tColor, vUv + t * vec2(-2.0, -2.0)).rgb;
  vec3 B = texture2D(tColor, vUv + t * vec2( 0.0, -2.0)).rgb;
  vec3 C = texture2D(tColor, vUv + t * vec2( 2.0, -2.0)).rgb;
  vec3 D = texture2D(tColor, vUv + t * vec2(-1.0, -1.0)).rgb;
  vec3 E = texture2D(tColor, vUv + t * vec2( 1.0, -1.0)).rgb;
  vec3 F = texture2D(tColor, vUv + t * vec2(-2.0,  0.0)).rgb;
  vec3 G = texture2D(tColor, vUv).rgb;
  vec3 H = texture2D(tColor, vUv + t * vec2( 2.0,  0.0)).rgb;
  vec3 I = texture2D(tColor, vUv + t * vec2(-1.0,  1.0)).rgb;
  vec3 J = texture2D(tColor, vUv + t * vec2( 1.0,  1.0)).rgb;
  vec3 K = texture2D(tColor, vUv + t * vec2(-2.0,  2.0)).rgb;
  vec3 L = texture2D(tColor, vUv + t * vec2( 0.0,  2.0)).rgb;
  vec3 M = texture2D(tColor, vUv + t * vec2( 2.0,  2.0)).rgb;
  vec3 c = (D + E + I + J) * 0.125
         + (A + B + F + G) * 0.03125
         + (B + C + G + H) * 0.03125
         + (F + G + K + L) * 0.03125
         + (G + H + L + M) * 0.03125;
  gl_FragColor = vec4(c, 1.0);
}
`;

const UP_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tColor;
uniform vec2 uTexel;     // destination texel
uniform float uRadius;
uniform float uScale;
varying vec2 vUv;
void main() {
  vec2 t = uTexel * uRadius;
  vec3 c = vec3(0.0);
  c += texture2D(tColor, vUv + vec2(-t.x,  t.y)).rgb * 1.0;
  c += texture2D(tColor, vUv + vec2( 0.0,  t.y)).rgb * 2.0;
  c += texture2D(tColor, vUv + vec2( t.x,  t.y)).rgb * 1.0;
  c += texture2D(tColor, vUv + vec2(-t.x,  0.0)).rgb * 2.0;
  c += texture2D(tColor, vUv).rgb * 4.0;
  c += texture2D(tColor, vUv + vec2( t.x,  0.0)).rgb * 2.0;
  c += texture2D(tColor, vUv + vec2(-t.x, -t.y)).rgb * 1.0;
  c += texture2D(tColor, vUv + vec2( 0.0, -t.y)).rgb * 2.0;
  c += texture2D(tColor, vUv + vec2( t.x, -t.y)).rgb * 1.0;
  gl_FragColor = vec4(c * (1.0 / 16.0) * uScale, 1.0);
}
`;

const STREAK_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tColor;
uniform vec2 uTexel;
uniform float uStride;
uniform vec3 uTint;
varying vec2 vUv;
void main() {
  vec3 c = vec3(0.0);
  float wsum = 0.0;
  for (int i = -6; i <= 6; i++) {
    float fi = float(i);
    float w = exp(-fi * fi / 18.0);
    c += texture2D(tColor, vUv + vec2(fi * uStride * uTexel.x, 0.0)).rgb * w;
    wsum += w;
  }
  gl_FragColor = vec4(c / wsum * uTint, 1.0);
}
`;

export class Bloom {
  constructor(blitter) {
    this.blit = blitter || new Blitter();
    this.mips = [];
    this.streakA = null; this.streakB = null;
    this.levels = 7;
    this.w = 0; this.h = 0;

    this.matPre = makeMaterial({
      name: 'postfx.bloom.prefilter',
      fragmentShader: PREFILTER_FRAG,
      uniforms: {
        tColor: { value: null }, tExposure: { value: null },
        uPreExposure: { value: 1 },
        uTexel: { value: new THREE.Vector2() },
        uThreshold: { value: 1.0 },
        uKnee: { value: 0.6 },
        // 48 was high enough that a single sodium-lamp specular could carry ~5
        // stops of energy into mip 6 on its own.
        uClamp: { value: 14.0 },
        uFirefly: { value: 3.0 },
      },
    });
    this.matDown = makeMaterial({
      name: 'postfx.bloom.down',
      fragmentShader: DOWN_FRAG,
      uniforms: { tColor: { value: null }, uTexel: { value: new THREE.Vector2() } },
    });
    this.matUp = makeMaterial({
      name: 'postfx.bloom.up',
      fragmentShader: UP_FRAG,
      uniforms: {
        tColor: { value: null }, uTexel: { value: new THREE.Vector2() },
        uRadius: { value: 1.0 }, uScale: { value: 1.0 },
      },
    });
    this.matUp.blending = THREE.AdditiveBlending;
    this.matUp.transparent = true;

    this.matStreak = makeMaterial({
      name: 'postfx.bloom.streak',
      fragmentShader: STREAK_FRAG,
      uniforms: {
        tColor: { value: null }, uTexel: { value: new THREE.Vector2() },
        uStride: { value: 1.0 }, uTint: { value: new THREE.Vector3(0.55, 0.7, 1.0) },
      },
    });
  }

  setSize(w, h, levels = 7) {
    if (this.w === w && this.h === h && this.levels === levels) return;
    this.w = w; this.h = h; this.levels = levels;
    for (const rt of this.mips) rt.dispose();
    this.mips = [];
    let mw = Math.max(1, w >> 1), mh = Math.max(1, h >> 1);
    for (let i = 0; i < levels; i++) {
      this.mips.push(makeRT(mw, mh, { name: `postfx.bloom.${i}` }));
      mw = Math.max(1, mw >> 1); mh = Math.max(1, mh >> 1);
      if (mw <= 2 || mh <= 2) { this.levels = i + 1; break; }
    }
    if (this.streakA) { this.streakA.dispose(); this.streakB.dispose(); }
    const si = Math.min(2, this.mips.length - 1);
    this.streakA = makeRT(this.mips[si].width, this.mips[si].height, { name: 'postfx.bloom.streakA' });
    this.streakB = makeRT(this.mips[si].width, this.mips[si].height, { name: 'postfx.bloom.streakB' });
  }

  get texture() { return this.mips[0].texture; }
  get streakTexture() { return this.streakA.texture; }

  render(renderer, { colorTexture, exposureTexture, preExposure, threshold, knee, radius, streak }) {
    const mips = this.mips;
    const n = mips.length;

    const p = this.matPre.uniforms;
    p.tColor.value = colorTexture;
    p.tExposure.value = exposureTexture;
    p.uPreExposure.value = (preExposure > 0 && isFinite(preExposure)) ? preExposure : 1;
    p.uTexel.value.set(1 / this.w, 1 / this.h);
    p.uThreshold.value = threshold;
    p.uKnee.value = knee;
    this.blit.draw(renderer, this.matPre, mips[0]);

    const d = this.matDown.uniforms;
    for (let i = 1; i < n; i++) {
      d.tColor.value = mips[i - 1].texture;
      d.uTexel.value.set(1 / mips[i - 1].width, 1 / mips[i - 1].height);
      this.blit.draw(renderer, this.matDown, mips[i]);
    }

    // anamorphic streak from a middle mip, before the upsample overwrites it
    if (streak > 0) {
      const si = Math.min(2, n - 1);
      const s = this.matStreak.uniforms;
      s.uTexel.value.set(1 / this.streakA.width, 1 / this.streakA.height);
      s.tColor.value = mips[si].texture;
      s.uStride.value = 1.0;
      this.blit.draw(renderer, this.matStreak, this.streakB);
      s.tColor.value = this.streakB.texture;
      s.uStride.value = 7.0;
      this.blit.draw(renderer, this.matStreak, this.streakA);
      s.tColor.value = this.streakA.texture;
      s.uStride.value = 25.0;
      this.blit.draw(renderer, this.matStreak, this.streakB);
      s.tColor.value = this.streakB.texture;
      s.uStride.value = 60.0;
      this.blit.draw(renderer, this.matStreak, this.streakA);
    }

    const u = this.matUp.uniforms;
    u.uRadius.value = radius;
    u.uScale.value = 1.0;
    for (let i = n - 1; i > 0; i--) {
      u.tColor.value = mips[i].texture;
      u.uTexel.value.set(1 / mips[i - 1].width, 1 / mips[i - 1].height);
      this.blit.draw(renderer, this.matUp, mips[i - 1]);
    }
  }

  dispose() {
    for (const rt of this.mips) rt.dispose();
    if (this.streakA) { this.streakA.dispose(); this.streakB.dispose(); }
    this.matPre.dispose(); this.matDown.dispose(); this.matUp.dispose(); this.matStreak.dispose();
  }
}
